import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Home,
  Plus,
  Search,
  ArrowLeft,
  Save,
  Trash2,
  Eye,
  DollarSign,
  MapPin,
  Bed,
  Bath,
  Maximize,
  Calendar,
  Users,
  ChevronRight,
  Scale
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (!value) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Listing Card Component
function ListingCard({ listing, onDelete }) {
  const statusColors = {
    active: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]",
    pending: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]",
    sold: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]",
    withdrawn: "bg-white/[0.1]/50 text-white/50"
  };

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] hover:border-gold/30 transition-all duration-300">
      <Link 
        to={`/realtor/listings/${listing.listing_id}`}
        className="block p-4"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-white font-medium">{listing.property_address}</h3>
            <p className="text-white/50 text-sm mt-1">
              {listing.property_type} • {listing.bedrooms}bd/{listing.bathrooms}ba • {listing.sqft?.toLocaleString()} sqft
            </p>
          </div>
          <span className={`px-2 py-1 text-xs font-bold ${statusColors[listing.status] || statusColors.active}`}>
            {listing.status?.toUpperCase()}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="font-mono text-xl font-bold text-gold">
            {formatCurrency(listing.asking_price)}
          </div>
          <div className="flex items-center gap-4 text-sm text-white/50">
            <span>{listing.days_on_market || 0} DOM</span>
            <span>{listing.showing_count || 0} showings</span>
            <span>{listing.offer_count || 0} offers</span>
          </div>
        </div>
      </Link>
      
      {/* Footer with actions */}
      <div className="px-4 pb-4 flex items-center justify-between border-t border-white/[0.08] pt-3 mt-0">
        {listing.investor_friendly && (
          <span className="text-xs bg-gold/20 text-gold px-2 py-1">
            Investor Friendly
          </span>
        )}
        {!listing.investor_friendly && <div />}
        
        {listing.offer_count > 0 && (
          <Link
            to={`/realtor/listings/${listing.listing_id}/compare`}
            className="flex items-center gap-1 text-gold text-sm hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Scale className="w-4 h-4" /> Compare Offers
          </Link>
        )}
      </div>
    </div>
  );
}

// New/Edit Listing Form
function ListingForm({ listing, onSave, onCancel }) {
  const [form, setForm] = useState({
    property_address: listing?.property_address || "",
    mls_number: listing?.mls_number || "",
    property_type: listing?.property_type || "SFH",
    bedrooms: listing?.bedrooms || 3,
    bathrooms: listing?.bathrooms || 2,
    sqft: listing?.sqft || 0,
    lot_size: listing?.lot_size || "",
    year_built: listing?.year_built || 2000,
    asking_price: listing?.asking_price || 0,
    arv: listing?.arv || 0,
    estimated_rent: listing?.estimated_rent || 0,
    hoa: listing?.hoa || 0,
    property_condition: listing?.property_condition || "Good",
    showing_instructions: listing?.showing_instructions || "",
    remarks_internal: listing?.remarks_internal || "",
    remarks_public: listing?.remarks_public || "",
    seller_motivation: listing?.seller_motivation || "",
    investor_friendly: listing?.investor_friendly || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Property Details */}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
        <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Property Details</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-white/50">Property Address *</Label>
            <Input
              value={form.property_address}
              onChange={(e) => setForm({...form, property_address: e.target.value})}
              placeholder="123 Main St, Austin, TX 78701"
              required
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>
          
          <div>
            <Label className="text-white/50">MLS #</Label>
            <Input
              value={form.mls_number}
              onChange={(e) => setForm({...form, mls_number: e.target.value})}
              placeholder="Optional"
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>
          
          <div>
            <Label className="text-white/50">Property Type</Label>
            <Select value={form.property_type} onValueChange={(v) => setForm({...form, property_type: v})}>
              <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SFH">Single Family</SelectItem>
                <SelectItem value="Condo">Condo</SelectItem>
                <SelectItem value="Townhouse">Townhouse</SelectItem>
                <SelectItem value="Multi-Family">Multi-Family</SelectItem>
                <SelectItem value="Land">Land</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-white/50">Beds</Label>
              <Input
                type="number"
                value={form.bedrooms}
                onChange={(e) => setForm({...form, bedrooms: parseInt(e.target.value) || 0})}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/50">Baths</Label>
              <Input
                type="number"
                step="0.5"
                value={form.bathrooms}
                onChange={(e) => setForm({...form, bathrooms: parseFloat(e.target.value) || 0})}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/50">Sqft</Label>
              <Input
                type="number"
                value={form.sqft}
                onChange={(e) => setForm({...form, sqft: parseInt(e.target.value) || 0})}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white/50">Lot Size</Label>
              <Input
                value={form.lot_size}
                onChange={(e) => setForm({...form, lot_size: e.target.value})}
                placeholder="0.25 acres"
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/50">Year Built</Label>
              <Input
                type="number"
                value={form.year_built}
                onChange={(e) => setForm({...form, year_built: parseInt(e.target.value) || 0})}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-white/50">Property Condition</Label>
            <Select value={form.property_condition} onValueChange={(v) => setForm({...form, property_condition: v})}>
              <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Excellent">Excellent</SelectItem>
                <SelectItem value="Good">Good</SelectItem>
                <SelectItem value="Fair">Fair</SelectItem>
                <SelectItem value="Poor">Poor</SelectItem>
                <SelectItem value="Needs Rehab">Needs Rehab</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
        <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Pricing</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-white/50">Asking Price ($) *</Label>
            <Input
              type="number"
              value={form.asking_price}
              onChange={(e) => setForm({...form, asking_price: parseFloat(e.target.value) || 0})}
              required
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white/50">ARV (After Repair Value)</Label>
            <Input
              type="number"
              value={form.arv}
              onChange={(e) => setForm({...form, arv: parseFloat(e.target.value) || 0})}
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white/50">Estimated Monthly Rent</Label>
            <Input
              type="number"
              value={form.estimated_rent}
              onChange={(e) => setForm({...form, estimated_rent: parseFloat(e.target.value) || 0})}
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white/50">HOA (Monthly)</Label>
            <Input
              type="number"
              value={form.hoa}
              onChange={(e) => setForm({...form, hoa: parseFloat(e.target.value) || 0})}
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>
        </div>
      </div>

      {/* Remarks */}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
        <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Remarks & Notes</h3>
        
        <div className="space-y-4">
          <div>
            <Label className="text-white/50">Public Remarks (visible to buyers)</Label>
            <textarea
              value={form.remarks_public}
              onChange={(e) => setForm({...form, remarks_public: e.target.value})}
              rows={3}
              className="w-full bg-black border border-white/[0.08] text-white p-3 mt-1"
              placeholder="Beautiful home in prime location..."
            />
          </div>
          <div>
            <Label className="text-white/50">Internal Notes (private)</Label>
            <textarea
              value={form.remarks_internal}
              onChange={(e) => setForm({...form, remarks_internal: e.target.value})}
              rows={3}
              className="w-full bg-black border border-white/[0.08] text-white p-3 mt-1"
              placeholder="Seller motivated, needs to close by..."
            />
          </div>
          <div>
            <Label className="text-white/50">Showing Instructions</Label>
            <textarea
              value={form.showing_instructions}
              onChange={(e) => setForm({...form, showing_instructions: e.target.value})}
              rows={2}
              className="w-full bg-black border border-white/[0.08] text-white p-3 mt-1"
              placeholder="Lockbox on front door, code 1234..."
            />
          </div>
          <div>
            <Label className="text-white/50">Seller Motivation</Label>
            <Select value={form.seller_motivation} onValueChange={(v) => setForm({...form, seller_motivation: v})}>
              <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                <SelectValue placeholder="Select motivation level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Very Motivated">Very Motivated</SelectItem>
                <SelectItem value="Motivated">Motivated</SelectItem>
                <SelectItem value="Normal">Normal</SelectItem>
                <SelectItem value="Testing Market">Testing Market</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Investor Settings */}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={form.investor_friendly}
            onCheckedChange={(checked) => setForm({...form, investor_friendly: checked})}
          />
          <div>
            <Label className="text-white">Mark as Investor Friendly</Label>
            <p className="text-white/50 text-sm">This listing will be visible to DealForge investors</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button type="button" onClick={onCancel} variant="outline" className="border-white/[0.1] text-white/70">
          <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
        </Button>
        <Button type="submit" disabled={saving} className="gold-gradient-btn rounded-none font-bold">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Listing"}
        </Button>
      </div>
    </form>
  );
}

// Main Listings Page
export default function RealtorListingsPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchListings();
  }, [statusFilter]);

  const fetchListings = async () => {
    try {
      const url = statusFilter === "all" 
        ? `${BACKEND_URL}/api/realtor/listings`
        : `${BACKEND_URL}/api/realtor/listings?status=${statusFilter}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setListings(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveListing = async (formData) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/realtor/listings`,
        formData,
        { withCredentials: true }
      );
      toast.success("Listing created!");
      setShowForm(false);
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    }
  };

  const filteredListings = listings.filter(l => 
    searchQuery === "" || 
    l.property_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (showForm) {
    return (
      <div className="min-h-screen bg-black p-8 ml-64">
        <h1 className="font-heading text-2xl font-bold text-white uppercase mb-6">New Listing</h1>
        <ListingForm onSave={handleSaveListing} onCancel={() => setShowForm(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-8 ml-64">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white uppercase">Listings</h1>
          <p className="text-white/50 mt-1">{listings.length} total listings</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gold-gradient-btn rounded-none font-bold">
          <Plus className="w-5 h-5 mr-2" /> New Listing
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search listings..."
            className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Listings Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredListings.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredListings.map((listing) => (
            <ListingCard key={listing.listing_id} listing={listing} />
          ))}
        </div>
      ) : (
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
          <Home className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-heading font-bold text-white mb-2">No Listings Found</h3>
          <p className="text-white/50 mb-6">
            {searchQuery ? "Try adjusting your search" : "Add your first listing to get started"}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowForm(true)} className="gold-gradient-btn rounded-none font-bold">
              <Plus className="w-5 h-5 mr-2" /> Add Listing
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
